import { useEffect, useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Camera, Paperclip, X, FileText, ExternalLink, AlertTriangle, Loader2, Check, Plus, Trash2,
    Copy, ChevronDown, ChevronRight, Snowflake, Building2,
} from 'lucide-react';
import { studyFormsAPI } from '@/lib/api';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// ===== Groupes froids par secteur (réplique du formulaire CEE d'origine) =====
const COLD_GROUP_FIELDS = [
    { type: 'text', key: 'marque', label: 'Marque', placeholder: 'Carrier, Trane, Daikin…' },
    { type: 'text', key: 'modele', label: 'Modèle', placeholder: '30HXC, RTAA…' },
    { type: 'text', key: 'numero_serie', label: 'N° de série (si visible)' },
    { type: 'text', key: 'fluide', label: 'Fluide frigorigène', placeholder: 'R134a, R410A, R1234ze…' },
    { type: 'number', key: 'puissance_kw', label: 'Puissance nominale (kW)' },
    { type: 'text', key: 'annee_mes', label: 'Année de mise en service', placeholder: '2018' },
    { type: 'select', key: 'type_condensation', label: 'Type de condensation', options: ['À air', 'À eau', 'Mixte / adiabatique'] },
    { type: 'select', key: 'etat_general', label: 'État général', options: ['Bon', 'Moyen', 'Dégradé'] },
    { type: 'select', key: 'hp_flottante_visible', label: 'HP flottante visible', options: ['Oui', 'Non', 'Non précisé'] },
    { type: 'select', key: 'bp_flottante_visible', label: 'BP flottante visible', options: ['Oui', 'Non', 'Non précisé'] },
    { type: 'text', key: 'mode_fonctionnement', label: 'Mode de fonctionnement', placeholder: 'Auto, manuel, local, à distance…' },
    { type: 'number', key: 'nombre_compresseurs', label: 'Nombre de compresseurs' },
];
const RELEVE_PARAMS = [
    { key: 'hp_instant', label: 'Haute pression instantanée', units: ['bar', '°C', '%'], defaultUnit: 'bar' },
    { key: 'bp_instant', label: 'Basse pression instantanée', units: ['bar', '°C', '%'], defaultUnit: 'bar' },
    { key: 't_aller_eau', label: 'Température aller eau', units: ['°C'], defaultUnit: '°C' },
    { key: 't_retour_eau', label: 'Température retour eau', units: ['°C'], defaultUnit: '°C' },
    { key: 'hpc_pct', label: 'Pourcentage HPC affiché', units: ['%'], defaultUnit: '%' },
    { key: 'alarmes', label: 'Alarmes affichées', units: ['—'], defaultUnit: '—' },
];
const COLD_PHOTO_TYPES = [
    { key: 'plaque', label: 'Plaque signalétique', required: true, hint: 'Marque, modèle, puissance, fluide, année' },
    { key: 'vue_generale', label: 'Vue générale du groupe', required: true, hint: "Photo d'ensemble + environnement" },
    { key: 'afficheur', label: 'Afficheur / Tablette', required: true, hint: 'Capture écran : HP, BP, T°, % HPC' },
    { key: 'schema', label: 'Schéma frigorifique mural', required: false, hint: 'Si présent sur site' },
    { key: 'local', label: "Vue d'ensemble local tech.", required: true, hint: 'Position du groupe + connexions' },
    { key: 'acces', label: 'Accès et environnement', required: true, hint: 'Portes, toiture, obstacles…' },
    { key: 'condenseur', label: 'Condenseur (si séparé)', required: false, hint: 'Si condenseur déporté' },
];

function ServerThumb({ studyId, file, onDelete, readOnly }) {
    const [url, setUrl] = useState(null);
    const [visible, setVisible] = useState(false);
    const containerRef = useRef(null);
    const isImg = (file.mime || '').startsWith('image/');

    // Lazy-load : on ne télécharge la photo que lorsqu'elle approche de l'écran
    // → réouverture d'un dossier rapide même avec beaucoup de photos.
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !isImg) return;
        const obs = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) { setVisible(true); obs.disconnect(); }
        }, { rootMargin: '300px' });
        obs.observe(el);
        return () => obs.disconnect();
    }, [isImg]);

    useEffect(() => {
        let revoked = null;
        if (isImg && visible) studyFormsAPI.fileObjectUrl(studyId, file.id).then((u) => { revoked = u; setUrl(u); }).catch(() => {});
        return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [studyId, file.id, isImg, visible]);
    const open = async () => {
        try { const u = isImg && url ? url : await studyFormsAPI.fileObjectUrl(studyId, file.id); window.open(u, '_blank'); } catch { /* ignore */ }
    };
    return (
        <div ref={containerRef} className="relative group rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`study-file-${file.id}`}>
            <button type="button" onClick={open} className="block w-full">
                {isImg ? (
                    <div className="aspect-square bg-slate-100 flex items-center justify-center">
                        {url ? <img src={url} alt={file.name} className="w-full h-full object-cover" /> : <Loader2 className="w-5 h-5 animate-spin text-slate-300" />}
                    </div>
                ) : (
                    <div className="aspect-square bg-slate-50 flex flex-col items-center justify-center gap-1 p-2">
                        <FileText className="w-8 h-8 text-slate-400" />
                        <span className="text-[10px] text-slate-500 line-clamp-2 text-center break-all">{file.name}</span>
                    </div>
                )}
            </button>
            <div className="absolute top-1 left-1 bg-emerald-500 text-white rounded-full p-0.5" title="Enregistré sur le serveur"><Check className="w-3 h-3" /></div>
            {!readOnly && (
                <button type="button" onClick={() => onDelete(file.id)} data-testid={`study-file-del-${file.id}`}
                    className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3.5 h-3.5 text-red-600" />
                </button>
            )}
        </div>
    );
}

function LocalThumb({ file, onDelete }) {
    const isImg = (file.mime || '').startsWith('image/');
    return (
        <div className="relative group rounded-lg border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid={`study-local-file-${file.clientFileId}`}>
            {isImg ? (
                <div className="aspect-square bg-slate-100 flex items-center justify-center"><img src={file.objectUrl} alt={file.name} className="w-full h-full object-cover" /></div>
            ) : (
                <div className="aspect-square bg-slate-50 flex flex-col items-center justify-center gap-1 p-2">
                    <FileText className="w-8 h-8 text-slate-400" />
                    <span className="text-[10px] text-slate-500 line-clamp-2 text-center break-all">{file.name}</span>
                </div>
            )}
            <div className="absolute top-1 left-1 bg-amber-500 text-white rounded-full p-0.5" title="En attente de synchronisation">
                {file.uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="text-[8px] px-1 font-bold">⏳</span>}
            </div>
            <button type="button" onClick={() => onDelete(file.clientFileId)}
                className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3.5 h-3.5 text-red-600" />
            </button>
        </div>
    );
}

// Galerie pour une catégorie donnée (filtre fileCtx par catégorie)
function FileGallery({ category, label, desc, max, isPhoto, fileCtx, readOnly }) {
    const serverFiles = (fileCtx.serverFiles || []).filter((f) => f.category === category);
    const localFiles = (fileCtx.localFiles || []).filter((f) => f.category === category);
    const count = serverFiles.length + localFiles.length;
    const inputId = `study-upload-${category}`;
    return (
        <div className="space-y-2" data-testid={`study-gallery-${category}`}>
            {label && (
                <div className="flex items-baseline justify-between">
                    <label className="text-sm font-medium text-slate-700">{label}</label>
                    {max ? <span className="text-xs text-slate-400">{count}/{max}</span> : null}
                </div>
            )}
            {desc && <p className="text-xs text-slate-500 -mt-1">{desc}</p>}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {serverFiles.map((f) => <ServerThumb key={f.id} studyId={fileCtx.studyId} file={f} onDelete={fileCtx.onDeleteServer} readOnly={readOnly} />)}
                {localFiles.map((f) => <LocalThumb key={f.clientFileId} file={f} onDelete={fileCtx.onDeleteLocal} />)}
                {!readOnly && (!max || count < max) && (
                    <label htmlFor={inputId}
                        className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors text-slate-400 hover:text-indigo-500">
                        {isPhoto ? <Camera className="w-6 h-6" /> : <Paperclip className="w-6 h-6" />}
                        <span className="text-[10px] font-medium">{isPhoto ? 'Photo' : 'Ajouter'}</span>
                        <input id={inputId} type="file" accept={isPhoto ? 'image/*' : undefined} multiple
                            className="hidden" data-testid={`study-input-${category}`}
                            onChange={(e) => { fileCtx.onAdd(category, Array.from(e.target.files || [])); e.target.value = ''; }} />
                    </label>
                )}
            </div>
        </div>
    );
}

// Champ simple (utilisé aussi pour les sous-champs des répétables)
function SimpleField({ field, value, onChange, readOnly }) {
    if (field.type === 'number' || field.type === 'text' || field.type === 'date') {
        return (
            <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">{field.label}{field.required && <span className="text-red-500"> *</span>}</label>
                <Input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    value={value ?? ''} placeholder={field.placeholder} disabled={readOnly}
                    onChange={(e) => onChange(field.key, e.target.value)} data-testid={`study-field-${field.key}`} />
            </div>
        );
    }
    if (field.type === 'select') {
        return (
            <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">{field.label}</label>
                <Select value={value || ''} onValueChange={(v) => onChange(field.key, v)} disabled={readOnly}>
                    <SelectTrigger data-testid={`study-field-${field.key}`}><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                    <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
            </div>
        );
    }
    return null;
}

// Éditeur "Groupes froids par secteur" — secteurs[] → groupes[] (12 champs + relevés + 7 photos)
function ColdGroupsField({ value, onChange, fieldKey, label, readOnly, fileCtx }) {
    const secteurs = Array.isArray(value) ? value : [];
    const [openSec, setOpenSec] = useState({});
    const [openGrp, setOpenGrp] = useState({});
    const setSecteurs = (next) => onChange(fieldKey, next);

    const addSecteur = () => {
        const id = uid();
        setSecteurs([...secteurs, { _id: id, nom: '', groupes: [] }]);
        setOpenSec((o) => ({ ...o, [id]: true }));
    };
    const removeSecteur = (sid) => {
        if (!window.confirm('Supprimer ce secteur et tous ses groupes froids ?')) return;
        setSecteurs(secteurs.filter((s) => s._id !== sid));
    };
    const patchSecteur = (sid, patch) => setSecteurs(secteurs.map((s) => (s._id === sid ? { ...s, ...patch } : s)));
    const patchGroupe = (sid, gid, patch) => setSecteurs(secteurs.map((s) => (s._id === sid
        ? { ...s, groupes: (s.groupes || []).map((g) => (g._id === gid ? { ...g, ...patch } : g)) } : s)));
    const addGroupe = (sid) => {
        const gid = uid();
        setSecteurs(secteurs.map((s) => (s._id === sid ? { ...s, groupes: [...(s.groupes || []), { _id: gid, releves: {} }] } : s)));
        setOpenGrp((o) => ({ ...o, [gid]: true }));
    };
    const removeGroupe = (sid, gid) => {
        if (!window.confirm('Supprimer ce groupe froid ?')) return;
        setSecteurs(secteurs.map((s) => (s._id === sid ? { ...s, groupes: (s.groupes || []).filter((g) => g._id !== gid) } : s)));
    };
    const duplicateGroupe = (sid, g) => {
        const gid = uid();
        const copy = { _id: gid, marque: g.marque, modele: g.modele, fluide: g.fluide, type_condensation: g.type_condensation, releves: {} };
        setSecteurs(secteurs.map((s) => (s._id === sid ? { ...s, groupes: [...(s.groupes || []), copy] } : s)));
        setOpenGrp((o) => ({ ...o, [gid]: true }));
    };
    const setReleve = (sid, gid, g, pkey, prop, val) => {
        const rel = { ...(g.releves || {}), [pkey]: { ...((g.releves || {})[pkey] || {}), [prop]: val } };
        patchGroupe(sid, gid, { releves: rel });
    };

    let groupCounter = 0;
    return (
        <div className="sm:col-span-2 space-y-3" data-testid={`study-coldgroups-${fieldKey}`}>
            {label && <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Snowflake className="w-4 h-4 text-blue-500" /> {label}</label>}
            {secteurs.length === 0 && (
                <p className="text-xs text-slate-400">Aucun secteur. Ajoutez un secteur (ex : Toiture, Local sous-sol…) puis ses groupes froids.</p>
            )}

            {secteurs.map((sec, si) => {
                const grpCount = (sec.groupes || []).length;
                const secOpen = openSec[sec._id] !== false;
                return (
                    <div key={sec._id} className="rounded-xl border-2 border-violet-300 bg-violet-50/30 overflow-hidden" data-testid={`secteur-card-${si}`}>
                        <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-violet-100/70 border-b border-violet-200">
                            <button type="button" onClick={() => setOpenSec((o) => ({ ...o, [sec._id]: secOpen ? false : true }))}
                                className="flex-shrink-0 p-1 text-violet-700 hover:bg-violet-200 rounded" data-testid={`btn-toggle-secteur-${si}`}>
                                {secOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                            </button>
                            <div className="flex-1 flex items-center gap-2 min-w-0">
                                <Building2 className="w-4 h-4 text-violet-700 flex-shrink-0" />
                                <input type="text" value={sec.nom || ''} disabled={readOnly}
                                    onChange={(e) => patchSecteur(sec._id, { nom: e.target.value })}
                                    placeholder={`Secteur ${si + 1} (ex : Toiture, Local sous-sol…)`}
                                    data-testid={`input-secteur-nom-${si}`}
                                    className="flex-1 min-w-0 bg-transparent border-b border-violet-300 text-violet-900 font-bold text-sm focus:outline-none focus:border-violet-600 placeholder-violet-400 disabled:opacity-70" />
                                <span className="text-[11px] text-violet-700 font-semibold whitespace-nowrap">{grpCount} groupe{grpCount > 1 ? 's' : ''}</span>
                            </div>
                            {!readOnly && (
                                <button type="button" onClick={() => removeSecteur(sec._id)} className="flex-shrink-0 p-1.5 text-red-500 hover:bg-red-100 rounded" data-testid={`btn-remove-secteur-${si}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {secOpen && (
                            <div className="p-3 space-y-3">
                                {grpCount === 0 && <p className="text-center py-3 text-sm text-slate-500 italic">Aucun groupe froid dans ce secteur. Cliquez sur « Ajouter un groupe froid » ci-dessous.</p>}
                                {(sec.groupes || []).map((g) => {
                                    groupCounter += 1;
                                    const gNum = groupCounter;
                                    const gOpen = openGrp[g._id] !== false;
                                    return (
                                        <div key={g._id} className="rounded-lg border-2 border-blue-200 bg-blue-50/40 overflow-hidden" data-testid={`groupe-froid-card-${gNum}`}>
                                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-100/60 border-b border-blue-200">
                                                <button type="button" onClick={() => setOpenGrp((o) => ({ ...o, [g._id]: gOpen ? false : true }))}
                                                    className="flex items-center gap-1.5 text-blue-800 font-semibold text-sm" data-testid={`btn-toggle-groupe-${gNum}`}>
                                                    {gOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                    <Snowflake className="w-4 h-4" /> Groupe froid #{gNum}
                                                    {g.marque ? <span className="font-normal text-blue-600">— {g.marque} {g.modele || ''}</span> : null}
                                                </button>
                                                {!readOnly && (
                                                    <div className="flex items-center gap-1">
                                                        <button type="button" onClick={() => duplicateGroupe(sec._id, g)} title="Dupliquer (copie marque/modèle/fluide, vide relevés & photos)"
                                                            className="p-1.5 text-blue-600 hover:bg-blue-200 rounded" data-testid={`btn-duplicate-groupe-${gNum}`}><Copy className="w-4 h-4" /></button>
                                                        <button type="button" onClick={() => removeGroupe(sec._id, g._id)}
                                                            className="p-1.5 text-red-500 hover:bg-red-100 rounded" data-testid={`btn-remove-groupe-${gNum}`}><Trash2 className="w-4 h-4" /></button>
                                                    </div>
                                                )}
                                            </div>

                                            {gOpen && (
                                                <div className="p-3 space-y-4">
                                                    {/* Caractéristiques */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {COLD_GROUP_FIELDS.map((sf) => (
                                                            <SimpleField key={sf.key} field={sf} value={g[sf.key]} readOnly={readOnly}
                                                                onChange={(k, v) => patchGroupe(sec._id, g._id, { [k]: v })} />
                                                        ))}
                                                    </div>

                                                    {/* Relevés */}
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Relevés (afficheur / tablette)</p>
                                                        <div className="space-y-1.5">
                                                            {RELEVE_PARAMS.map((p) => {
                                                                const rv = (g.releves || {})[p.key] || {};
                                                                return (
                                                                    <div key={p.key} className="flex items-center gap-2">
                                                                        <span className="text-xs text-slate-600 flex-1 min-w-0">{p.label}</span>
                                                                        <Input type="text" value={rv.value ?? ''} disabled={readOnly}
                                                                            onChange={(e) => setReleve(sec._id, g._id, g, p.key, 'value', e.target.value)}
                                                                            placeholder="Valeur" className="w-24 h-8 text-sm" data-testid={`releve-${gNum}-${p.key}`} />
                                                                        <Select value={rv.unit || p.defaultUnit} onValueChange={(v) => setReleve(sec._id, g._id, g, p.key, 'unit', v)} disabled={readOnly}>
                                                                            <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
                                                                            <SelectContent>{p.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {/* Photos du groupe */}
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Photos du groupe #{gNum}</p>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            {COLD_PHOTO_TYPES.map((pt) => (
                                                                <FileGallery key={pt.key}
                                                                    category={`froid__${sec._id}__${g._id}__${pt.key}`}
                                                                    label={`${pt.label}${pt.required ? ' *' : ''}`} desc={pt.hint} max={4}
                                                                    isPhoto fileCtx={fileCtx} readOnly={readOnly} />
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Observations */}
                                                    <div>
                                                        <label className="text-xs font-medium text-slate-600 block mb-1">Observations techniques particulières</label>
                                                        <Textarea rows={2} value={g.observations || ''} disabled={readOnly}
                                                            onChange={(e) => patchGroupe(sec._id, g._id, { observations: e.target.value })}
                                                            placeholder="Anomalies, bruits, fuites, encrassement, signes d'usure…" data-testid={`groupe-obs-${gNum}`} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {!readOnly && (
                                    <Button type="button" variant="outline" size="sm" onClick={() => addGroupe(sec._id)} className="w-full gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`btn-add-groupe-${si}`}>
                                        <Plus className="w-4 h-4" /> Ajouter un groupe froid à « {sec.nom || `Secteur ${si + 1}`} »
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {!readOnly && (
                <Button type="button" variant="outline" onClick={addSecteur} className="w-full gap-1.5 border-dashed border-violet-400 text-violet-700 hover:bg-violet-50" data-testid="btn-add-secteur">
                    <Plus className="w-4 h-4" /> Ajouter un secteur
                </Button>
            )}
        </div>
    );
}

export default function StudyField({ field, value, onChange, readOnly, fileCtx }) {
    const fullCls = field.full ? 'sm:col-span-2' : '';

    if (field.type === 'cold_groups') {
        return <ColdGroupsField value={value} onChange={onChange} fieldKey={field.key} label={field.label} readOnly={readOnly} fileCtx={fileCtx} />;
    }

    if (field.type === 'info') {
        const warn = field.variant === 'warning';
        return (
            <div className={`sm:col-span-2 rounded-lg p-3 text-sm flex gap-2 ${warn ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-slate-50 border border-slate-200 text-slate-600'}`}>
                {warn && <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                <span>{field.text}</span>
            </div>
        );
    }

    if (field.type === 'link') {
        return (
            <div className="sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => window.open(field.url, '_blank')} className="gap-2" data-testid="study-geoportail-link">
                    <ExternalLink className="w-4 h-4" /> {field.label}
                </Button>
            </div>
        );
    }

    if (field.type === 'photo' || field.type === 'file') {
        return (
            <div className="sm:col-span-2">
                <FileGallery category={field.category} label={field.label} desc={field.desc} max={field.max}
                    isPhoto={field.type === 'photo'} fileCtx={fileCtx} readOnly={readOnly} />
            </div>
        );
    }

    if (field.type === 'repeater') {
        const items = Array.isArray(value) ? value : [];
        const setItem = (idx, key, val) => onChange(field.key, items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
        const add = () => onChange(field.key, [...items, { _id: uid() }]);
        const remove = (idx) => onChange(field.key, items.filter((_, i) => i !== idx));
        return (
            <div className="sm:col-span-2 space-y-3" data-testid={`study-repeater-${field.key}`}>
                {items.length === 0 && <p className="text-xs text-slate-400">Aucun élément. Cliquez sur « {field.addLabel} ».</p>}
                {items.map((it, idx) => (
                    <div key={it._id || idx} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-3" data-testid={`study-repeater-item-${field.key}-${idx}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-700">{field.label} #{idx + 1}</span>
                            {!readOnly && (
                                <button type="button" onClick={() => remove(idx)} className="text-red-500 hover:text-red-700" data-testid={`study-repeater-del-${field.key}-${idx}`}><Trash2 className="w-4 h-4" /></button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {field.itemFields.map((sf) => (
                                <SimpleField key={sf.key} field={sf} value={it[sf.key]} onChange={(k, v) => setItem(idx, k, v)} readOnly={readOnly} />
                            ))}
                        </div>
                        {field.photoPrefix && (
                            <FileGallery category={`${field.photoPrefix}__${it._id}`} label={field.photoLabel} max={field.photoMax}
                                isPhoto fileCtx={fileCtx} readOnly={readOnly} />
                        )}
                    </div>
                ))}
                {!readOnly && (
                    <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1.5" data-testid={`study-repeater-add-${field.key}`}>
                        <Plus className="w-4 h-4" /> {field.addLabel}
                    </Button>
                )}
            </div>
        );
    }

    if (field.type === 'text' || field.type === 'number' || field.type === 'date') {
        return (
            <div className={fullCls}>
                <label className="text-sm font-medium text-slate-700 block mb-1">{field.label}{field.required && <span className="text-red-500"> *</span>}</label>
                <Input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    value={value ?? ''} placeholder={field.placeholder} disabled={readOnly}
                    onChange={(e) => onChange(field.key, e.target.value)} data-testid={`study-field-${field.key}`} />
            </div>
        );
    }

    if (field.type === 'textarea') {
        return (
            <div className={fullCls}>
                <label className="text-sm font-medium text-slate-700 block mb-1">{field.label}</label>
                <Textarea value={value ?? ''} placeholder={field.placeholder} disabled={readOnly} rows={3}
                    onChange={(e) => onChange(field.key, e.target.value)} data-testid={`study-field-${field.key}`} />
            </div>
        );
    }

    if (field.type === 'select') {
        return (
            <div className={fullCls}>
                <label className="text-sm font-medium text-slate-700 block mb-1">{field.label}</label>
                <Select value={value || ''} onValueChange={(v) => onChange(field.key, v)} disabled={readOnly}>
                    <SelectTrigger data-testid={`study-field-${field.key}`}><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                    <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
            </div>
        );
    }

    if (field.type === 'toggle') {
        return (
            <div className="sm:col-span-2 flex items-start gap-3 rounded-lg border border-slate-200 p-3">
                <Checkbox checked={value === true} disabled={readOnly} onCheckedChange={(c) => onChange(field.key, c === true)} data-testid={`study-field-${field.key}`} className="mt-0.5" />
                <div>
                    <div className="text-sm font-medium text-slate-700">{field.label}</div>
                    {field.hint && <div className="text-xs text-slate-500">{field.hint}</div>}
                </div>
            </div>
        );
    }

    if (field.type === 'cards') {
        return (
            <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-2">{field.label}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {field.options.map((o) => {
                        const selected = value === o.value;
                        return (
                            <button type="button" key={o.value} disabled={readOnly || o.disabled}
                                onClick={() => onChange(field.key, selected ? '' : o.value)}
                                data-testid={`study-card-${field.key}-${o.value}`}
                                className={`text-left rounded-lg border p-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${selected ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 hover:border-indigo-300 bg-white'}`}>
                                <div className={`text-sm font-medium ${selected ? 'text-indigo-700' : 'text-slate-800'}`}>{o.label}</div>
                                {o.desc && <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (field.type === 'checks') {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (v) => onChange(field.key, arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
        if (field.cards) {
            return (
                <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700 block mb-2">{field.label}</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {field.options.map((o) => {
                            const selected = arr.includes(o.value);
                            return (
                                <button type="button" key={o.value} disabled={readOnly} onClick={() => toggle(o.value)}
                                    data-testid={`study-check-${field.key}-${o.value}`}
                                    className={`text-left rounded-lg border p-3 transition-all disabled:opacity-60 ${selected ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 hover:border-indigo-300 bg-white'}`}>
                                    <div className={`text-sm font-medium ${selected ? 'text-indigo-700' : 'text-slate-800'}`}>{o.label}</div>
                                    {o.desc && <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return (
            <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-2">{field.label}</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {field.options.map((o) => (
                        <label key={o.value} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 cursor-pointer hover:bg-slate-50">
                            <Checkbox checked={arr.includes(o.value)} disabled={readOnly} onCheckedChange={() => toggle(o.value)} data-testid={`study-check-${field.key}-${o.value}`} />
                            <span className="text-sm text-slate-700">{o.label}</span>
                        </label>
                    ))}
                </div>
            </div>
        );
    }

    return null;
}
